import * as i0 from '@angular/core';
import { Injectable } from '@angular/core';
import { isString } from '@egovsolutions/type-checking';
import { merge } from 'rxjs';
import { filter } from 'rxjs/operators';
import * as i1 from '@egovsolutions/angular-dialogs-service';

class CanDeactivateGuard {
    constructor(
    // Dependencies
    dialogs) {
        // Bindings
        this.dialogs = dialogs;
        // Page rules
        this.pageRules = [];
        // Dialog rules
        this.dialogRules = [];
        this.dialogEventsSubscriptions = {};
        this.beforeUnload = this.beforeUnload.bind(this);
        // Initialize
        this.init();
    }
    // Initialization
    init() {
        this.initializeBeforeUnload();
    }
    addPage(rule) {
        this.addPageRule(rule);
        const canDeactivate = () => {
            if (this.pageRules.includes(rule)) {
                const question = this.getQuestion(rule);
                return !question || this.confirm(question);
            }
            return true;
        };
        return canDeactivate;
    }
    removePage(rule) {
        this.removePageRule(rule);
    }
    addPageRule(rule) {
        this.pageRules.push(rule);
    }
    removePageRule(rule) {
        this.pageRules = this.pageRules.filter(r => r !== rule);
    }
    addDialog(dialogRef, rule, customHandler) {
        this.dialogRules.push(rule);
        const closeAttempt = (value) => {
            if (this.dialogRules.includes(rule)) {
                const question = this.getQuestion(rule, true);
                const close = () => {
                    if (customHandler)
                        customHandler(value);
                    else
                        dialogRef.close(value);
                };
                if (!question)
                    close();
                else
                    this.confirm(question).then(val => { if (val)
                        close(); });
            }
        };
        const escapeEvents$ = dialogRef.keydownEvents().pipe(filter(e => e.key === 'Escape'));
        dialogRef.disableClose = true;
        this.dialogEventsSubscriptions[dialogRef.id] = merge(dialogRef.backdropClick(), escapeEvents$)
            .subscribe(() => closeAttempt(undefined));
        dialogRef.beforeClosed().subscribe(() => this.removeDialog(dialogRef, rule));
        return closeAttempt;
    }
    removeDialog(dialogRef, rule) {
        this.dialogRules = this.dialogRules.filter(r => r !== rule);
        dialogRef.disableClose = false;
        const subscriptions = this.dialogEventsSubscriptions[dialogRef.id];
        if (subscriptions)
            subscriptions.unsubscribe();
    }
    // Deactivation handler
    canDeactivate(component) {
        return component?.canDeactivate ? component.canDeactivate() : true;
    }
    // Unload handling
    initializeBeforeUnload() {
        window.addEventListener('beforeunload', this.beforeUnload);
    }
    beforeUnload(e) {
        for (const rule of this.dialogRules.concat(this.pageRules)) {
            if (rule()) {
                e.preventDefault();
                e.returnValue = 'Confirm exit?';
                return e.returnValue;
            }
        }
    }
    // Question
    getQuestion(rule, dialog) {
        let question = rule();
        if (question && !isString(question))
            question = dialog ? 'Discard dialog?' : 'Leave page?';
        return question;
    }
    // Confirm
    confirm(question) {
        const promise = new Promise((resolve, reject) => {
            const dialogRef = this.dialogs.dialog({
                navigate: false,
                content: question,
                buttons: [
                    {
                        label: 'Cancel',
                        closeState: false,
                    },
                    {
                        label: 'Ok',
                        default: true,
                        closeState: true,
                    },
                ],
                autoFocus: true
            });
            dialogRef.afterClosed().subscribe(resolve);
        });
        return promise;
    }
}
CanDeactivateGuard.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: CanDeactivateGuard, deps: [{ token: i1.DialogsService }], target: i0.ɵɵFactoryTarget.Injectable });
CanDeactivateGuard.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: CanDeactivateGuard, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "13.1.2", ngImport: i0, type: CanDeactivateGuard, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: function () { return [{ type: i1.DialogsService }]; } });

/*
 * Public API Surface of angular-can-deactivate-guard
 */

/**
 * Generated bundle index. Do not edit.
 */

export { CanDeactivateGuard };
